/** 캔버스 UI용 문자열 포맷 + 검증 (DOM/HTML 없음) */

import { deriveByRandomness } from '../shared/minionsLogic';

export interface StatusHudModel {
  bettingSeconds: number | null;
  nextBlock: string | number | null;
  bettingClosed: boolean;
  hint?: string;
  /** 서버 action=status 의 betting_allowed (false면 전광판 마감 문구) */
  serverBettingAllowed?: boolean | null;
  /** PHP __bettingTimerHoldAtZero — 0초에서 마감 문구 유지 */
  bettingHoldAtZero?: boolean;
  /** false면 사다리 연출 중 등으로 대기 전광판 숨김 */
  scoreboardVisible?: boolean;
}

export type PatternTabId = 'odd_even' | 'left_right' | 'ladder';

/** ethminions.php `scoreboard-block` 와 동일 */
export function formatScoreboardBlockLine(
  nextBlock: string | number | null | undefined
): string {
  if (nextBlock != null && String(nextBlock).trim() !== '') {
    return `다음 기다리는 블록 번호: #${String(nextBlock)}`;
  }
  return '다음 기다리는 블록 번호: 불러오는 중...';
}

/** ethminions.php `renderCountdown` / scoreboard-countdown 과 동일 규칙 */
export function formatScoreboardCountdown(m: StatusHudModel): string {
  const n = m.bettingSeconds;
  if (n != null && Number.isFinite(n)) {
    const safe = Math.max(0, Math.floor(n));
    /* 서버가 betting_allowed=false 를내도 남은 초가 있으면 초를 표시 — 실제 타이머와 전광판 불일치 방지 */
    if (safe > 0) {
      return `남은 시간: ${safe}초`;
    }
    /* 0초 + 마감 플래그면 "0초"와 "마감"이 번갈아 나오는 깜빡임 방지 */
    if (
      safe === 0 &&
      (m.bettingClosed || m.serverBettingAllowed === false || m.bettingHoldAtZero)
    ) {
      return '남은 시간: 마감 (다음 블록 대기)';
    }
  }
  if (m.serverBettingAllowed === false) {
    return '남은 시간: 마감 (다음 블록 대기)';
  }
  if (n == null || !Number.isFinite(n)) return '로딩중...';
  const safe = Math.max(0, Math.floor(n));
  if (m.bettingHoldAtZero && safe === 0) {
    return '남은 시간: 마감 (다음 블록 대기)';
  }
  return `남은 시간: ${safe}초`;
}

/** 전광판 중간 줄 — 고정 "대기 중..." 대신 남은 초·마감·동기화에 맞춤 */
export function formatScoreboardStatusLine(m: StatusHudModel): string {
  if (m.hint && /실패|확인|프록시|없음|응답|동기화 실패/i.test(m.hint)) {
    return '연결 확인 중...';
  }
  const n = m.bettingSeconds;
  if (n != null && Number.isFinite(n) && Math.floor(n) > 0) {
    return '이번 라운드 배팅 진행 중';
  }
  const line = formatScoreboardCountdown(m);
  if (line.includes('마감')) {
    return '다음 블록 채결까지 대기';
  }
  if (line === '로딩중...') {
    return '상태 불러오는 중...';
  }
  return '대기 중...';
}

export function formatStatusCanvas(m: StatusHudModel): string {
  const sec =
    m.bettingSeconds != null && Number.isFinite(m.bettingSeconds)
      ? `${Math.max(0, Math.floor(m.bettingSeconds))}초`
      : '—';
  const next =
    m.nextBlock != null && m.nextBlock !== '' ? `#${String(m.nextBlock)}` : '—';
  const closed = m.bettingClosed ? '\n(마감·다음 블록 대기)' : '';
  const lines = [`남은 시간: ${sec}${closed}`, `다음 블록: ${next}`];
  if (m.hint) lines.push(`\n${m.hint}`);
  return lines.join('\n');
}

function rowLine(r: Record<string, unknown>): string {
  const block = r.block_number ?? r.block ?? '-';
  const start =
    r.start === 'left' ? '좌' : r.start === 'right' ? '우' : String(r.start ?? '-');
  const lines = Number(r.lines) === 3 ? 3 : Number(r.lines) === 4 ? 4 : r.lines ?? '-';
  const res =
    r.result === 'odd' ? '홀' : r.result === 'even' ? '짝' : String(r.result ?? '-');
  return `#${block} · ${start} · ${lines}줄 · ${res}`;
}

export function formatHistoryCanvas(
  records: Record<string, unknown>[],
  maxLines: number
): string {
  if (!records.length) return '기록 없음 · API/프록시 확인';
  const sorted = [...records].sort(
    (a, b) =>
      Number(b.block_number ?? b.block ?? 0) - Number(a.block_number ?? a.block ?? 0)
  );
  return sorted
    .slice(0, maxLines)
    .map((r) => rowLine(r))
    .join('\n');
}

export interface HistoryStats {
  odd: number;
  even: number;
  left: number;
  right: number;
  line3: number;
  line4: number;
}

export function computeHistoryStats(records: Record<string, unknown>[]): HistoryStats {
  let odd = 0;
  let even = 0;
  let left = 0;
  let right = 0;
  let line3 = 0;
  let line4 = 0;
  for (const r of records) {
    if (r.result === 'odd') odd++;
    else if (r.result === 'even') even++;
    if (r.start === 'left') left++;
    else if (r.start === 'right') right++;
    if (Number(r.lines) === 3) line3++;
    else if (Number(r.lines) === 4) line4++;
  }
  return { odd, even, left, right, line3, line4 };
}

export function patternTabVisibility(tab: PatternTabId): {
  oddEven: boolean;
  leftRight: boolean;
  ladder: boolean;
} {
  if (tab === 'left_right') {
    return { oddEven: false, leftRight: true, ladder: false };
  }
  if (tab === 'ladder') {
    return { oddEven: false, leftRight: false, ladder: true };
  }
  return { oddEven: true, leftRight: false, ladder: false };
}

export function formatStatsCanvas(
  stats: HistoryStats,
  tab: PatternTabId
): string {
  const vis = patternTabVisibility(tab);
  const parts: string[] = [];
  if (vis.oddEven) parts.push(`홀 ${stats.odd}   짝 ${stats.even}`);
  if (vis.leftRight) parts.push(`좌 ${stats.left}   우 ${stats.right}`);
  if (vis.ladder) parts.push(`3줄 ${stats.line3}   4줄 ${stats.line4}`);
  return parts.join('\n') || '—';
}

export function verifyRandomnessInput(raw: string): string {
  const d = deriveByRandomness(raw);
  if (!d) {
    return '0x hex 랜덤값을 입력하세요.';
  }
  const startKr = d.start === 'left' ? '좌' : '우';
  const resKr = d.result === 'odd' ? '홀' : '짝';
  return `출발 ${startKr} · ${d.lines}줄 · ${resKr}`;
}

/** ethminions.php initVerificationPanel / run() 과 동일 문구 */
export function formatVerifyResultEthminions(raw: string): string {
  const d = deriveByRandomness(raw);
  if (!d) {
    return '올바른 랜덤값을 입력해주세요. (예: 0xabc...)';
  }
  const startLabel = d.start === 'left' ? '좌(Left)' : '우(Right)';
  const resultLabel = d.result === 'odd' ? '홀(Odd)' : '짝(Even)';
  const sl = d.secondLast ?? '';
  return `마지막 글자(${d.last}) → ${startLabel}, 두 번째 글자(${sl}) → ${d.lines}줄, 최종 결과 → ${resultLabel}`;
}

/** API/WS 행을 패턴 격자·내역 카드용으로 통일 (ethminions.php mlHistory 와 동일 필드 의미) */
export interface HistoryRowNorm {
  block: number;
  start: 'left' | 'right';
  lines: 3 | 4;
  result: 'odd' | 'even';
  createdAtMs: number | null;
}

export function parseHistoryRows(records: Record<string, unknown>[]): HistoryRowNorm[] {
  const out: HistoryRowNorm[] = [];
  for (const r of records) {
    const block = Number(r.block_number ?? r.block ?? r.round ?? 0);
    if (!Number.isFinite(block) || block <= 0) continue;
    const start = r.start === 'right' ? 'right' : 'left';
    const lines = Number(r.lines) === 4 ? 4 : 3;
    const result = r.result === 'even' ? 'even' : 'odd';
    let createdAtMs: number | null = null;
    const ca = r.created_at;
    if (ca != null) {
      const t = Date.parse(String(ca));
      if (Number.isFinite(t)) createdAtMs = t;
    }
    out.push({ block, start, lines, result, createdAtMs });
  }
  return out;
}

export function sortHistoryChronological(rows: HistoryRowNorm[]): HistoryRowNorm[] {
  return [...rows].sort((a, b) => a.block - b.block);
}

export function sortHistoryNewestFirst(rows: HistoryRowNorm[]): HistoryRowNorm[] {
  return [...rows].sort((a, b) => b.block - a.block);
}

/** ethminions addResultItem 배지 (날짜+블록 끝자리) */
export function historyBadgeLabel(row: HistoryRowNorm): string {
  if (row.createdAtMs != null) {
    const d = new Date(row.createdAtMs);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${mm}월${dd}일-${String(row.block).slice(-4)}`;
  }
  return `#${row.block}`;
}

export type PatternColorKey = 'blue' | 'red' | 'gray';

export const PATTERN_CELL_PX = 26;
export const PATTERN_GRID_ROWS = 10;
const PATTERN_HEADER_ROWS = 1;

export interface PatternBoardModel {
  dots: Array<{ cx: number; cy: number; color: PatternColorKey; roundLabel: string }>;
  heads: Array<{ cx: number; cy: number; label: string; color: PatternColorKey }>;
  boardW: number;
  boardH: number;
  footer: string;
  emptyMessage: string | null;
}

/** ethminions.php `updateAnalysisGrid` 와 동일 규칙(열=연속 구간, 행=동일 토큰 스택) */
export function buildPatternBoardModel(
  chronological: HistoryRowNorm[],
  tab: PatternTabId
): PatternBoardModel {
  const cell = PATTERN_CELL_PX;
  const rows = PATTERN_GRID_ROWS;
  const header = PATTERN_HEADER_ROWS;

  let footer: string;
  let tokenOf: (r: HistoryRowNorm) => { key: string; label: string; color: PatternColorKey };
  if (tab === 'left_right') {
    footer = '좌우 패턴 흐름';
    tokenOf = (r) =>
      r.start === 'left'
        ? { key: 'L', label: '좌', color: 'blue' }
        : { key: 'R', label: '우', color: 'red' };
  } else if (tab === 'ladder') {
    footer = '사다리 수 패턴 흐름';
    tokenOf = (r) =>
      r.lines === 3
        ? { key: '3', label: '3', color: 'blue' }
        : { key: '4', label: '4', color: 'red' };
  } else {
    footer = '홀짝 패턴 흐름';
    tokenOf = (r) =>
      r.result === 'odd'
        ? { key: 'O', label: '홀', color: 'blue' }
        : { key: 'E', label: '짝', color: 'red' };
  }

  if (!chronological.length) {
    return {
      dots: [],
      heads: [],
      boardW: cell * 4,
      boardH: (rows + header) * cell,
      footer: '데이터가 없습니다.',
      emptyMessage: '기록된 회차가 없습니다.',
    };
  }

  let x = 0;
  let y = 0;
  let prevKey: string | null = null;
  const dots: PatternBoardModel['dots'] = [];
  const heads: PatternBoardModel['heads'] = [];
  const headAtCol = new Set<number>();

  for (let idx = 0; idx < chronological.length; idx++) {
    const r = chronological[idx];
    const { key, label, color } = tokenOf(r);
    if (key === prevKey) {
      y = Math.min(rows - 1, y + 1);
    } else {
      x += prevKey === null ? 0 : 1;
      y = 0;
      prevKey = key;
    }
    const cx = x * cell + cell / 2;
    const cy = (y + header) * cell + cell / 2;
    const rs = String(r.block);
    const roundLabel = rs.length >= 4 ? rs.slice(-4) : rs;
    dots.push({ cx, cy, color, roundLabel });
    if (!headAtCol.has(x)) {
      heads.push({ cx, cy: cell / 2, label, color });
      headAtCol.add(x);
    }
  }

  const boardW = Math.max(cell * 4, (x + 2) * cell);
  const boardH = (rows + header) * cell;
  return { dots, heads, boardW, boardH, footer, emptyMessage: null };
}
